/**
 * Utility functions for handling attendance hours calculations with threshold
 */

/**
 * Calculates the minimum remaining hours from CRM info array
 * @param {Array} crmInfo - Array of CRM info objects with remaining_hours property
 * @returns {number|null} - Minimum remaining hours or null if no valid hours found
 */
export const getMinimumRemainingHours = (crmInfo) => {
  if (!crmInfo || !Array.isArray(crmInfo) || crmInfo.length === 0) {
    return null
  }

  const validHours = crmInfo
    .map((info) => info.remaining_hours)
    .filter((hours) => hours !== null && hours !== undefined)
    // Exclude non-numeric strings like 'None'
    .filter((hours) => {
      const n = Number.parseFloat(hours)
      return !Number.isNaN(n) && Number.isFinite(n)
    })
    .map((hours) => Number.parseFloat(hours))

  if (validHours.length === 0) {
    return null
  }

  return Math.min(...validHours)
}

/**
 * Calculates adjusted remaining hours by subtracting threshold value
 * @param {number|null} remainingHours - Original remaining hours
 * @param {number} threshold - Threshold value (should be negative, e.g., -2)
 * @returns {number|null} - Adjusted remaining hours or null if input is null
 */
export const calculateAdjustedRemainingHours = (remainingHours, threshold) => {
  if (remainingHours === null || remainingHours === undefined) {
    return null
  }

  const numericHours = Number.parseFloat(remainingHours)
  const numericThreshold = Number.parseFloat(threshold)
  if (Number.isNaN(numericHours) || Number.isNaN(numericThreshold)) {
    return null
  }
  return numericHours - numericThreshold
}

/**
 * Checks if attendance can be marked based on remaining hours and threshold
 * @param {number|null} remainingHours - Original remaining hours
 * @param {number|null} threshold - Threshold value (should be negative, e.g., -2). If null, defaults to 0
 * @returns {boolean} - True if attendance can be marked, false otherwise
 */
export const canMarkAttendance = (remainingHours, threshold = null) => {
  if (remainingHours === null || remainingHours === undefined) {
    return false
  }

  const numericHours = Number.parseFloat(remainingHours)
  if (Number.isNaN(numericHours)) {
    return false
  }

  // If threshold is provided, check if remainingHours > threshold
  // Otherwise, default to checking if remainingHours > 0
  const thresholdValue =
    threshold !== null && threshold !== undefined
      ? Number.parseFloat(threshold)
      : 0

  if (Number.isNaN(thresholdValue)) {
    return numericHours > 0
  }

  return numericHours > thresholdValue
}

/**
 * Converts decimal hours to hours and minutes format
 * @param {number} decimalHours - Hours in decimal format (e.g., 2.5)
 * @returns {string} - Formatted string like "2h 30m" or "45m" or "0m"
 */
export const formatHoursToQuarter = (decimalHours) => {
  if (decimalHours === null || decimalHours === undefined) {
    return 'No hours data'
  }

  const numericHours = Number.parseFloat(decimalHours)
  if (Number.isNaN(numericHours) || !Number.isFinite(numericHours)) {
    return 'No hours data'
  }

  const roundedToQuarter = Math.round(numericHours * 4) / 4
  const formattedValue = roundedToQuarter
    .toFixed(2)
    .replace(/\.00$/, '')
    .replace(/\.([0-9])0$/, '.$1')

  return `${formattedValue} hr(s)`
}

/**
 * Gets the display text for remaining hours (directly, no threshold)
 * @param {number|null} remainingHours - Original remaining hours
 * @returns {string} - Display text for remaining hours
 */
export const getRemainingHoursDisplayText = (remainingHours) => {
  const formattedTime = formatHoursToQuarter(remainingHours)
  if (formattedTime === 'No hours data') {
    return formattedTime
  }

  return `${formattedTime} remaining`
}

/**
 * Gets all valid sale orders with their remaining hours (filters out null/None)
 * @param {Array} crmInfo - Array of CRM info objects with remaining_hours property
 * @returns {Array} - Array of sale order objects with remaining hours
 */
export const getValidSaleOrders = (crmInfo) => {
  if (!crmInfo || !Array.isArray(crmInfo) || crmInfo.length === 0) {
    return []
  }

  return crmInfo
    .filter((info) => {
      const hours = info.remaining_hours
      if (hours === null || hours === undefined || hours === 'None') {
        return false
      }
      const n = Number.parseFloat(hours)
      return !Number.isNaN(n) && Number.isFinite(n)
    })
    .map((info) => ({
      saleOrderId: info.sale_order_id,
      consumerId: info.consumer_id,
      consumerName: info.consumer_name,
      customerId: info.customer_id,
      customerName: info.customer_name,
      studentId: info.student_id,
      remainingHours: Number.parseFloat(info.remaining_hours),
    }))
}

/**
 * Processes batch data to add calculated remaining hours information
 * @param {Object} batch - Batch object with crm_info array
 * @param {number} threshold - Threshold value (should be negative, e.g., -2)
 * @returns {Object} - Batch object with additional calculated properties
 */
export const processBatchRemainingHours = (batch, threshold) => {
  if (!batch || !batch.crm_info) {
    return {
      ...batch,
      minimumRemainingHours: null,
      adjustedRemainingHours: null,
      canMarkAttendance: false,
      remainingHoursDisplayText: 'No hours data',
    }
  }

  const minimumHours = getMinimumRemainingHours(batch.crm_info)
  const adjustedHours = calculateAdjustedRemainingHours(minimumHours, threshold)
  const canMark = canMarkAttendance(minimumHours)
  const displayText = getRemainingHoursDisplayText(minimumHours)

  return {
    ...batch,
    minimumRemainingHours: minimumHours,
    adjustedRemainingHours: adjustedHours,
    canMarkAttendance: canMark,
    remainingHoursDisplayText: displayText,
  }
}
